import type { PnlReport, PnlRow } from '@erp/shared';
import Decimal from 'decimal.js';
import { AlertTriangle, Landmark, TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/shadcn/index.js';
import { EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney } from '../../lib/money.js';

import { KpiCard } from './_shared/KpiCard.js';
import { PeriodPicker, rangeForPreset, type PeriodValue } from './_shared/PeriodPicker.js';

export function PnLPage(): JSX.Element {
  const { t, i18n } = useTranslation('reports');
  const [period, setPeriod] = useState<PeriodValue>(() => {
    const r = rangeForPreset('mtd');
    return { preset: 'mtd', ...r };
  });
  const [report, setReport] = useState<PnlReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setReport(null);
    void api()
      .get<PnlReport>(`/reports/profit-and-loss?from=${period.from}&to=${period.to}`)
      .then((r) => setReport(r.data))
      .catch((e) => setError(String((e as Error).message ?? e)));
  }, [period.from, period.to]);

  const fmt = (amt: string): string =>
    report ? formatMoney(amt, { locale: i18n.language, currency: report.baseCurrencyCode }) : '—';

  const totalRev = report ? new Decimal(report.totalRevenue) : null;
  const netInc = report ? new Decimal(report.netIncome) : null;
  const marginPercent = totalRev && totalRev.gt(0) && netInc
    ? netInc.dividedBy(totalRev).times(100).toFixed(1)
    : null;

  const hasActivity = report && (report.revenue.length > 0 || report.expenses.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('pnl.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('pnl.description')}</p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </header>

      {error ? (
        <Card className="border-danger/40 bg-danger/5 p-4">
          <div className="flex items-start gap-2 text-sm text-danger" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
            <div className="flex-1">
              <p className="font-medium">{t('pnl.loadFailed')}</p>
              <p className="mt-0.5 text-xs text-danger/80">{error}</p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <KpiCard
              tone="success"
              icon={TrendingUp}
              label={t('pnl.totalRevenue')}
              value={fmt(report?.totalRevenue ?? '0')}
              loading={!report}
            />
            <KpiCard
              tone="warning"
              icon={TrendingDown}
              label={t('pnl.totalExpenses')}
              value={fmt(report?.totalExpenses ?? '0')}
              loading={!report}
            />
            <KpiCard
              tone={netInc && netInc.gte(0) ? 'brand' : 'danger'}
              icon={Landmark}
              label={t('pnl.netIncome')}
              value={fmt(report?.netIncome ?? '0')}
              hint={marginPercent ? `${t('pnl.marginPercent')} ${marginPercent}%` : undefined}
              loading={!report}
            />
          </div>

          {!report ? (
            <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>
          ) : !hasActivity ? (
            <EmptyState
              icon={<TrendingUp className="h-8 w-8" aria-hidden />}
              title={t('pnl.empty')}
              description={t('pnl.emptyDescription')}
            />
          ) : (
            <>
              <Section
                title={t('pnl.revenue')}
                rows={report.revenue}
                total={report.totalRevenue}
                fmt={fmt}
                tone="success"
                t={t}
              />
              <Section
                title={t('pnl.expenses')}
                rows={report.expenses}
                total={report.totalExpenses}
                fmt={fmt}
                tone="warning"
                t={t}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  rows: PnlRow[];
  total: string;
  fmt: (amt: string) => string;
  tone: 'success' | 'warning';
  t: (k: string) => string;
}

function Section({ title, rows, total, fmt, tone, t }: SectionProps): JSX.Element {
  const totalN = new Decimal(total);
  const denom = totalN.abs().lte(0) ? new Decimal(1) : totalN.abs();
  const spineClass = tone === 'success' ? 'before:bg-success' : 'before:bg-warning';
  const barBg = tone === 'success' ? 'bg-success/60' : 'bg-warning/60';
  return (
    <Card
      className={`relative overflow-hidden p-0 before:absolute before:inset-y-3 before:start-0 before:w-1 before:rounded-full ${spineClass}`}
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-border bg-bg-subtle/40 px-4 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
        <span className="font-mono text-sm font-semibold tabular-nums">{fmt(total)}</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">{t('pnl.columns.code')}</TableHead>
            <TableHead>{t('pnl.columns.name')}</TableHead>
            <TableHead className="w-1/3 text-end">{t('pnl.columns.amount')}</TableHead>
            <TableHead className="w-40 text-end">{t('pnl.columns.share')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const amt = new Decimal(r.baseAmount);
            const sharePct = denom.gt(0)
              ? Math.min(100, amt.abs().dividedBy(denom).times(100).toNumber())
              : 0;
            return (
              <TableRow key={r.accountId}>
                <TableCell className="font-mono text-xs">{r.accountCode}</TableCell>
                <TableCell className="font-medium">{r.accountName}</TableCell>
                <TableCell className="text-end font-mono tabular-nums">{fmt(r.baseAmount)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-subtle">
                      <div
                        className={`h-full rounded-full ${barBg}`}
                        style={{ width: `${sharePct.toFixed(1)}%` }}
                        aria-hidden
                      />
                    </div>
                    <span className="w-10 text-end font-mono text-xs tabular-nums">
                      {sharePct.toFixed(0)}%
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
